<template>
    <div>
        <vue-headful :title="'Incidents By Division [Reports] :: MeritEHS'" />

        <h4 class="py-3 mb-3 d-print-none">
            <ol class="breadcrumb font-weight-bold m-0">
                <li class="breadcrumb-item active">
                    Incidents Totals
                    <span v-if="y_axis_name"> - By {{ y_axis_name }}</span>
                </li>
            </ol>
        </h4>

        <div v-if="report.id" class="mb-3 d-print-none">
            <span class="h5" :title="`Saved Report Applied: ${report.report_name}`"><i class="fa fa-save"></i> {{ report.report_name }}</span>
            -
            <b-button-group size="sm">
                <b-button variant="default" title="Remove Saved Report" @click="clearReport()"><i class="fa fa-remove"></i></b-button>
                <template v-if="report.user_id == $store.state.user.user_id || $store.state.user.is_admin">
                    <b-button variant="default" title="Update Saved Report" @click="showSave()"><i class="fa fa-edit"></i></b-button>
                    <b-button variant="default" title="Delete Saved Report" @click="showDelete()"><i class="fa fa-trash text-danger"></i></b-button>
                </template>
            </b-button-group>
        </div>

        <div :class="pdf_view ? 'container' : ''" :style="pdf_view ? 'max-width: 980px;' : ''">
            <div class="row d-none d-print-block mb-4" v-if="pdfLogo" style="margin-bottom: -20px; z-index: 100;">
                <div class="col-md-3">
                    <img :src="pdfLogo" class="img-fluid" />
                </div>
            </div>

            <div class="alert alert-info d-print-none" v-if="pdf_view">
                <strong>How to Export as PDF:</strong>
                <ol>
                    <li>On your browser, there should be a "File" or "Options" tab with a "Print" option. You may also hold down the "Ctrl" key and press the "P" key.</li>
                    <li>This will bring up the print page options box. There should be an option called "Select Printer" or "Destination".</li>
                    <li>Select "Save as PDF" or "Adobe PDF".</li>
                    <li>Click "Save" or "Print". This will open your file explorer and let you save it to your computer as a PDF.</li>
                </ol>
            </div>

            <div class="card mb-3 d-print-none">
                <div class="card-body">
                    <div class="d-inline-block">
                        <multi-select v-model="filters['group_by']" :btnLabel="(values) => ('Group By' + (values.length > 0 ? ' - ' + values[0].name : ''))" :selectOptions="groupOptions" :options="{multi: false}"></multi-select>
                    </div>
                    <div class="d-inline-block">
                        <multi-select v-model="filters['display']" :btnLabel="(values) => ('Display' + (values.length > 0 ? ' - ' + values[0].name : ''))" :selectOptions="displayOptions" :options="{multi: false}"></multi-select>
                    </div>
                    <div class="d-inline-block">
                        <multi-select v-model="filters['interval']" :btnLabel="(values) => ('Interval' + (values.length > 0 ? ' - ' + values[0].name : ''))" :selectOptions="intervalOptions" :options="{multi: false}"></multi-select>
                    </div>
                    <div class="d-inline-block">
                        <multi-select v-model="filters['IsContractor']" :btnLabel="(values) => ('Subcontractor' + (values.length > 0 ? ' - ' + values[0].name : ''))" :selectOptions="subOptions" :options="{multi: false}"></multi-select>
                    </div>
                    <div class="d-inline-block">
                        <multi-select v-model="filters['incident_types']" :btnLabel="(values) => ('Incident Types ('+values.length+')')" :selectOptions="incidentTypeOptions" :options="{multi: true}"></multi-select>
                    </div>
                    <div class="d-inline-block">
                        <multi-select v-model="filters['divisions']" :btnLabel="(values) => ('Divisions ('+values.length+')')" :selectOptions="divisionOptions" :options="{multi: true}"></multi-select>
                    </div>
                    <div class="d-inline-block">
                        <date-range-selector ref="inciDate" buttonTitle="Incident Date" v-model="filters['date_range']" />
                    </div>
                </div>
                <div class="card-footer">
                    <button type="button" class="btn btn-sm btn-warning" v-on:click="getData()" :disabled="loading">
                        <i class="fa fa-list"></i> Build
                    </button>

                    <button type="button" class="btn btn-sm btn-success" v-on:click="getData(true)" :disabled="loading">
                        <i class="fa fa-file-excel-o"></i> Export
                    </button>

                    <b-button-group>
                        <b-dropdown variant="default" size="sm" split v-on:click="showSave(true)">
                            <template #button-content>
                                <i class="fa fa-save"></i>
                                <span class="badge badge-warning" :title="`${saved_reports.length} Saved Reports`" v-if="saved_reports.length > 0">{{ saved_reports.length }}</span>
                            </template>
                            <template v-if="saved_reports.length > 0">
                                <b-dropdown-item v-for="saved_report in saved_reports" :key="saved_report.id" @click="setSavedReport(saved_report.id)" :title="`View Report: ${saved_report.report_name}`">
                                    <span class="badge badge-warning" v-if="saved_report.is_global" title="This is a Globally Shared Filter"><i class="fa fa-globe"></i></span> {{ saved_report.report_name }}
                                </b-dropdown-item>
                            </template>
                            <b-dropdown-item v-else disabled>-- No Saved Reports --</b-dropdown-item>
                        </b-dropdown>
                    </b-button-group>

                    <div class="d-inline-block ml-2">
                        <b-form-checkbox v-model="filters.hide_empty" :value="true" :unchecked-value="false" plain class="ml-2">
                            Hide Empty <i class="fa fa-question-circle" title="Checking this option will remove incident that do not have a value for the selected Group By Criteria"></i>
                        </b-form-checkbox>
                    </div>
                    <div class="d-inline-block ml-2">
                        <b-form-checkbox v-model="pdf_view" :value="true" :unchecked-value="false" plain class="ml-2">PDF View</b-form-checkbox>
                    </div>
                </div>
            </div>

            <report-incident-totals-display ref="totalsDisplay" :filters="filters" :dateLabel="dateLabel" :report="report" />
        </div>

        <b-modal v-model="save_report"
            ref="saveReport"
            :title="(report.id ? 'Update' : 'New') + ' Saved Report'"
            size="md"
            no-fade
            :no-close-on-backdrop="true"
            ok-variant="success"
            :ok-title="(report.id ? 'Update' : 'Create') + ' Saved Report'"
            @ok="saveReport">
            <div class="form-group">
                <label class="col-form-label" :class="{'text-danger': errors.report_name}">
                    Report Name: <i class="fa fa-asterisk text-danger small"></i>
                </label>
                <div class="">
                    <b-form-input v-model="report.report_name" :class="{'is-invalid': errors.report_name}" placeholder="Enter Report Name" />
                </div>
            </div>

            <div class="form-group">
                <label class="col-form-label" :class="{'text-danger': errors.report_name}">
                    Is Global: <i class="fa fa-question-circle" title="Global Saved Reports can be used by other MeritEHS Users. They can also be used in building Custom Dashboards."></i>
                </label>
                <div class="">
                    <b-form-checkbox v-model="report.is_global" :value="1" :unchecked-value="0" plain />
                </div>
            </div>
        </b-modal>

        <b-modal v-model="delete_report"
            ref="deleteReport"
            :title="'Delete Report'"
            size="md"
            no-fade
            :no-close-on-backdrop="true"
            ok-variant="danger"
            :ok-title="'Delete Report'"
            @ok="deleteReport">
            <div class="alert alert-danger">
                <strong>Warning:</strong> You are about to delete this saved report. This is a permanent action and cannot be undone.
            </div>

            <div v-if="in_use_dashboards.length > 0">
                <div class="alert alert-warning">
                    This Saved Report is in used on the following dashboard(s). Remove it from all dashboards before deleting:
                    <ol class="mt-2">
                        <li v-for="dash in in_use_dashboards">
                            <a :href="`/dashboards/settings/view/${dash.id}`" target="_blank" title="View Dashboard in a New Tab">{{ dash.dashboard_name }}</a>
                        </li>
                    </ol>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
    import filterExtractor from '@/helpers/TableFilterExtractor';

    export default {
        data: function() {
            return {
                loading: false,
                hide_empty: false,
                save_report: false,
                delete_report: false,
                pdf_view: false,
                show_filters: true,
                all_time_total: null,
                filterCount: 0,
                pdfLogo: this.$store.state.user.pdf_logo,
                report: {},
                errors: {},
                saved_reports: [],
                in_use_dashboards: [],

                displayOptions: [
                    {id: 'table', name: 'Table'},
                    {id: 'graph', name: 'Graph - Bar'},
                    {id: 'graph_column', name: 'Graph - Column'}
                ],

                intervalOptions: [
                    {id: null, name: 'No Interval'},
                    {id: 'inci_month_year', name: 'Monthly'},
                    {id: 'inci_year', name: 'Yearly'}
                ],

                groupOptions: [
                    {id: 'DivisionId', name: 'Division'},
                    {id: 'MajorBodyPartId', name: 'Major Body Part'},
                    {id: 'SpecificBodyPartId', name: 'Specific Body Part'},
                    {id: 'position', name: 'Employee Title'},
                    {id: 'supervisor_id', name: 'Supervisor'},
                    {id: 'ContractorCompany', name: 'Subcontractor'},
                    {id: 'DayOfWeek', name: 'Day Of Week'},
                    {id: 'MonthOfYear', name: 'Month of Year'},
                    //{id: 'TotalIncidents', name: 'Total Incidents'},
                ],

                subOptions: [
                    {id: null, name: 'N/A'},
                    {id: '1', name: 'Subcontractors Only'},
                    {id: '0', name: 'Exclude Subcontractors'},
                ],

                filters: {
                    'display': [{'name': 'Table', 'id': 'table'}],
                    'group_by': [{'name': 'Division', 'id': 'DivisionId'}],
                    'interval': [{'name': 'Yearly', 'id': 'Year'}],
                    'IsContractor': [{'name': 'N/A', 'id': null}],
                    'incident_types': [],
                    'injury_types': [],
                    'divisions': [],
                    'jobsite.JobsiteId': [],
                    'date_range': null,
                    'hide_empty': false,
                },

                divisions: [],
                incident_types: [],
                x_axis_totals: [],
                results: [],

                dateLabel: null,
            }
        },

        computed: {
            labels(){
                //Setup
                return Object.keys(this.x_axis_totals);
            },

            series(){
                //Setup
                var series = [];

                //Build each
                for (var i = 0; i < this.results.length; i++)
                {
                    //Build the Data
                    var data = [];

                    //Setup
                    var keys = Object.keys(this.results[i].data);

                    //Build Each
                    for (var j = 0; j < keys.length; j++)
                    {
                        //Push the Data Point
                        data.push({y: this.results[i].data[keys[j]]});
                    }

                    //Append
                    series.push({
                        name: (this.results[i][this.y_axis_name] ? this.results[i][this.y_axis_name] : '-- No '+this.y_axis_name+'--'),
                        data: data,
                    });
                }

                //Send it
                return series;
            },

            options(){
                //Build the Title
                var title = 'Incidents By '+this.y_axis_name;

                //Check for Saved Report
                if(this.report.report_name)
                {
                    //Set Title
                    title = this.report.report_name;
                }

                //Send it
                return {
                    chart: {
                        type: 'bar'
                    },
                    title: {
                        text: title,
                    },
                    subtitle: {
                            text: (this.dateLabel ? this.dateLabel : '')
                        },
                    xAxis: {
                        categories: this.labels
                    },
                    yAxis: {
                        title: {
                            text: 'Incidents By '+this.y_axis_name
                        }
                    },
                    plotOptions: {
                        bar: {
                            dataLabels: {
                                enabled: true,
                                formatter: function(){
                                    return this.point.series.name+': '+this.point.y;
                                }
                            }
                        }
                    },
                    legend: {
                        layout: 'horizontal',
                        align: 'right',
                        verticalAlign: 'bottom',
                        x: 0,
                        y: 0,
                        floating: false,
                        borderColor: '#dddddd',
                        borderWidth: 1,
                    },
                    series: this.series,
                    // tooltip: {
                    //     formatter: function() {
                    //         //Send it
                    //         var html = 'Incident Type: <b>'+ this.series.name + '</b><br/>' +
                    //                 'Date: <b>'+ this.x + '</b><br/>' +
                    //                 'Total Incidents: <b>'+ this.y + '</b><br/>';

                    //         //Check for empty or undefined
                    //         if(typeof this.point.options.injuries == 'undefined' || Object.keys(this.point.options.injuries).length == 0)
                    //         {
                    //             return html;
                    //         }

                    //         //Setup
                    //         var elemKeys = Object.keys(this.point.options.injuries);

                    //         //Loop and Add items
                    //         for(const key of elemKeys)
                    //         {
                    //             //Helper
                    //             var elem = this.point.options.injuries[key];

                    //             //Append the HTML
                    //             html += elem.text + ': <b>' + elem.total + '</b><br>';
                    //         };

                    //         //Send it
                    //         return html;
                    //     }
                    // },
                }
            },

            divisionOptions(){
                //Setup
                var options = [];

                //Loop and Build
                for (var i = 0; i < this.divisions.length; i++)
                {
                    //Push
                    options.push({id: this.divisions[i].DivisionId, name: this.divisions[i].division_name});
                }

                //Send it
                return options;
            },

            incidentTypeOptions(){
                //Setup
                var options = [];

                //Loop and Build
                for (var i = 0; i < this.incident_types.length; i++)
                {
                    //Push
                    options.push({id: this.incident_types[i].IncidentTypeId, name: this.incident_types[i].TypeName});
                }

                //Send it
                return options;
            },

            y_axis_name(){
                return this.filters['group_by'][0].name;
            },

            display(){
                //Send it
                return this.filters['display'][0].id;
            },
        },

        watch: {
            pdf_view(newVal, oldVal){
                //Helper?
                this.loading = true;

                //Redraw the Draw
                this.getData();
            },

            // filters: {
            //     deep: true,
            //     handler(){
            //         //Refresh
            //         this.getData();
            //     },
            // },

            // 'filters.date_range'(newVal, oldVal){
            //     //Set the label
            //     this.setDateLabel();
            // },

            // 'filters.group_by'(newVal, oldVal){
            //     //Fetch Data
            //     this.getData();
            // },

            // 'filters.interval'(newVal, oldVal){
            //     //Fetch Data
            //     this.getData();
            // },
        },

        methods: {
            setDateLabel(){
                //Set the Label
                this.dateLabel = this.$refs.inciDate.getLabel();
            },

            getData(export_results = false) {
                //Set it
                this.$refs.totalsDisplay.getData(export_results);
            },

            getFilters() {
                //Send the response
                this.$http
                    .get('reports/incidents/totals?filters=true', this.filters)
                    .then(response => {
                        //Set all the data
                        this.divisions = response.data.divisions;
                        this.incident_types = response.data.incident_types;
                        this.saved_reports = response.data.saved_reports;
                    })
                    .catch(response => {

                    })
                    .then(() => {
                        //Set loading
                        this.loading = false;
                    });
            },

            buildFilters(){
                //Setup
                var filters = filterExtractor.extractFilters(this.filters);

                //Append the Various Inputs
                filters.y_axis_id = this.filters['group_by'][0].id;
                filters.y_axis_name = this.y_axis_name;
                filters.x_axis_time = this.filters['interval'][0].id;

                //Send it
                return filters;
            },

            clearReport(){
                //Simply Reset the REport
                this.report = {};
            },

            showSave(reset = false){
                //Check it
                if(reset)
                {
                    this.clearReport();
                }

                //Set it
                this.save_report = true;
            },

            setSavedReport(report_id){
                //Setup
                var report_to_apply = null;

                //Get the report from the saved
                for (var i = 0; i < this.saved_reports.length; i++)
                {
                    //Compare IDs
                    if(this.saved_reports[i].id == report_id)
                    {
                        report_to_apply = this.saved_reports[i];
                    }
                }

                //Check Empty
                if(!report_to_apply)
                {
                    return;
                }

                //Set the report
                this.report = report_to_apply;

                //Apply the Filters
                this.filters = JSON.parse(report_to_apply.report_filters);

                //Build it
                this.getData();
            },

            saveReport(bvEvt){
                //Stops the modal from closing
                bvEvt.preventDefault();

                //Reset the errors
                this.errors = {};

                //Set Some Helper
                this.report.report_type = 'IncidentTotals';

                //Set the filters
                //this.report.report_filters = this.buildFilters();
                this.report.report_filters = this.filters;

                //Helper Flag
                var is_new = this.report.id ? false : true;

                //Send it
                this.$http
                .post('reports/save', this.report)
                .then(response => {
                    //Alert the user?
                    this.$toastr.success('Report Saved');

                    //Close the modal
                    this.save_report = false;

                    //Set it
                    this.report = response.data.report;

                    //Push or Update Saved
                    if(is_new)
                    {
                        //Push it
                        this.saved_reports.push(response.data.report);
                    }
                    else
                    {
                        for (var i = 0; i < this.saved_reports.length; i++)
                        {
                            //Check it
                            if(this.saved_reports[i].id == response.data.report.id)
                            {
                                //Update it
                                this.saved_reports.splice(i, 1, response.data.report);
                            }
                        }
                    }
                })
                .catch(error => {
                    //Check for error
                    if(error.response.status === 422)
                    {
                        //Display validation errors
                        this.errors = error.response.data;
                    }
                });
            },

            showDelete(){
                //Clear
                this.in_use_dashboards = [];

                //Delete
                this.delete_report = true;
            },

            deleteReport(bvEvt){
                //Stops the modal from closing
                bvEvt.preventDefault();

                //Reset the errors
                this.errors = {};

                //Send it
                this.$http
                .post('reports/delete/'+this.report.id)
                .then(response => {
                    //Alert the user?
                    this.$toastr.success('Report Delete');

                    //Close the modal
                    this.delete_report = false;

                    for (var i = 0; i < this.saved_reports.length; i++)
                    {
                        //Check it
                        if(this.saved_reports[i].id == this.report.id)
                        {
                            //Update it
                            this.saved_reports.splice(i, 1);
                        }
                    }
                })
                .catch(error => {
                    //Check for error
                    if(error.response.status === 422)
                    {
                        //Display validation errors
                        this.errors = error.response.data;
                    }
                    else if (error.response && error.response.status === 418)
                    {
                        //We use 418 to display the existing dashboards
                        this.in_use_dashboards = error.response.data;
                    }
                });
            },
        },

        mounted() {
            //Set the Date Label
            this.setDateLabel();

            //Loda the Filters
            this.getFilters();

            //Graph it
            this.getData();

        }
    }
</script>

<style type="text/css" scoped>
    @media print{
        /*@page {
            size: landscape;
        }*/

        #highcharts-canvas {
            width: 2300;
        }

        .card {
            background-color: transparent;
            border: none;
        }
    }
</style>

